import React, { useEffect } from "react";
import { Form, Modal, DatePicker } from "antd";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import "./TargetAchievementsModal.scss";

// 🟢 Reusable components
import InputField from "../../../Reuseable/InputField/InputField";
import CustomSelect from "../../../Reuseable/Select/Select";
import NormalButton from "../../../Reuseable/NormalButton/NormalButton";
import { TARGET_TYPE_CHOICES, toTargetTypeValue } from "../targetTypes";

dayjs.extend(customParseFormat);

const { RangePicker } = DatePicker;

/* The list endpoint hands dates back as "Sep 8, 2026" in some responses and
   ISO in others, so both are accepted before giving up. */
const toDay = (value) => {
  if (!value) return null;
  const direct = dayjs(value);
  if (direct.isValid()) return direct;
  const loose = dayjs(value, ["MMM D, YYYY", "DD MMM YYYY", "YYYY-MM-DD"]);
  return loose.isValid() ? loose : null;
};

const TargetAchievementsModal = ({
  isOpen,
  onClose,
  userData = {},
  onSave,
  loading = false,
}) => {
  const [form] = Form.useForm();

  useEffect(() => {
    if (userData) {
      /* The rows come from the targets endpoint as user_name /
         target_type_name / target_value / start_date / end_date. This form
         read user / target / targetValue / startDate, none of which exist on
         them, so every field opened blank. Both spellings are accepted. */
      const startDate = toDay(userData.start_date ?? userData.startDate);
      const endDate = toDay(userData.end_date ?? userData.endDate);

      form.setFieldsValue({
        user: userData.user_name ?? userData.user ?? "",
        target: toTargetTypeValue(
          userData.target_type ?? userData.target_type_name ?? userData.target
        ),
        targetValue: userData.target_value ?? userData.targetValue,
        duration: startDate && endDate ? [startDate, endDate] : undefined,
      });
    } else {
      form.resetFields();
    }
  }, [userData, form]);

  const handleSave = () => {
    form.validateFields().then(async (values) => {
      const [startDate, endDate] = values.duration || [];

      /* The endpoint wants target_type back even though it is not editable
         here, so the row's existing type is sent unchanged. It is read off
         the record rather than the form, since a disabled field is not
         something to trust for a value the server will store. */
      const target_type =
        toTargetTypeValue(
          userData?.target_type ?? userData?.target_type_name ?? userData?.target
        ) ||
        /* a code the choice list does not know about is echoed back as it
           was stored rather than dropped from the request */
        userData?.target_type ||
        values.target;

      const payload = {
        start_date: startDate?.format("YYYY-MM-DD"),
        end_date: endDate?.format("YYYY-MM-DD"),
        target_type,
        target_value: Number(values.targetValue),
      };

      /* The dialog used to close the moment Submit was pressed, before the
         save was attempted - a failure closed it just the same. It now waits
         and stays open if the page reports the update did not go through. */
      const saved = await onSave?.(userData?.id, payload);
      if (saved !== false) onClose();
    });
  };

  return (
    <Modal
      open={isOpen}
      onCancel={onClose}
      footer={null}
      title="Edit Target"
      className="targetAchievementModalContainer"
      centered
    >
      <div className="targetModalWrapper">
        {/* who this target belongs to - the read-only user field alone did
            not make that obvious */}
        {(userData?.user_name || userData?.user) && (
          <div className="targetModalContext">
            <span className="targetModalContext__label">Target for</span>
            <span className="targetModalContext__name">
              {userData.user_name || userData.user}
            </span>
          </div>
        )}

        <Form layout="vertical" form={form}>
          {/* Duration - editable */}
          <Form.Item
            label="Duration"
            name="duration"
            className="formItem"
            rules={[{ required: true, message: "Please select a date range" }]}
          >
            <RangePicker style={{ width: "100%" }} format="DD/MM/YYYY" />
          </Form.Item>

          {/* User - fixed for the life of the target */}
          <Form.Item name="user" className="formItem">
            <InputField label="Users" disabled />
          </Form.Item>

          {/* Target type - fixed; editing a target must not change what is
              being measured, only how much and over what period */}
          <Form.Item name="target" className="formItem">
            <CustomSelect
              label="Select Target"
              placeholder="Select Target"
              options={TARGET_TYPE_CHOICES}
              disabled
            />
          </Form.Item>

          {/* Target Value - editable */}
          <Form.Item
            name="targetValue"
            className="formItem"
            rules={[{ required: true, message: "Please enter a value" }]}
          >
            <InputField
              label="Target Value"
              placeholder="Enter target value"
              type="number"
            />
          </Form.Item>

          {/* Submit button */}
          <div className="submitBtn">
            <button
              type="button"
              className="targetModalCancel"
              onClick={onClose}
            >
              Cancel
            </button>
            <NormalButton type="primary" onClick={handleSave} loading={loading}>
              Save Changes
            </NormalButton>
          </div>
        </Form>
      </div>
    </Modal>
  );
};

export default TargetAchievementsModal;
